import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type {
  OnyxDropdownOptionsSource,
  OnyxOptional,
  OnyxTab,
} from '@onyx/angular';
import {
  DropdownHelper,
  OnyxButtonComponent,
  OnyxDatePipe,
  OnyxDividerComponent,
  OnyxDropdownComponent,
  OnyxEmptyStateComponent,
  OnyxFilterOperator,
  OnyxFormMode,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxLogoComponent,
  OnyxMessageComponent,
  OnyxTabsComponent,
  OnyxToastService,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { chain } from 'lodash';
import type { Subject } from 'rxjs';
import { EMPTY, map, startWith, switchMap } from 'rxjs';
import { AuthStore } from '../../../../../../auth/common/stores/auth.store';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { RouteHelper } from '../../../../../../common/helpers/route.helper';
import { ValidationContext } from '../../../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { FleetIdentifierPipe } from '../../../../../../common/pipes/fleet-identifier.pipe';
import { FleetState } from '../../../../../fleet/common/enums/fleet-state';
import { FleetValidationHelper } from '../../../../../fleet/common/helpers/fleet-validation.helper';
import { FleetHelper } from '../../../../../fleet/common/helpers/fleet.helper';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../../../fleet/common/interfaces/fleet';
import { FleetService } from '../../../../../fleet/common/services/fleet.service';
import { OrderPlanningMode } from '../../../../common/enums/order-planning-mode';
import { OrderStatus } from '../../../../common/enums/order-status';
import { OrdersQueryParamKey } from '../../../../common/enums/orders-query-param-key';
import { OrdersRoute } from '../../../../common/enums/orders-route';
import { OrdersRouterStateKey } from '../../../../common/enums/orders-router-state-key';
import { OrderModalHelper } from '../../../../common/helpers/order-modal.helper';
import { OrderHelper } from '../../../../common/helpers/order.helper';
import type {
  AssignationData,
  Order,
} from '../../../../common/interfaces/order';
import type { SimplifiedRoutePoint } from '../../../../common/interfaces/simplified-route-point';
import type { OrderManualRouteResolverData } from '../../../../common/resolvers/order-manual-route.resolver';
import { OrdersService } from '../../../../common/services/orders.service';
import { OrderModalDeadlineMessageComponent } from '../../../order-modal-deadline-message/order-modal-deadline-message.component';
import { OrderModalAssignmentComponent } from '../order-modal-assignment/order-modal-assignment.component';
import { CommonHelper } from './../../../../../../common/helpers/common.helper';
import { OrderModalOutsourcingFormComponent } from './order-modal-outsourcing-form/order-modal-outsourcing-form.component';

@Component({
  selector: 'app-order-modal-summary-main-section',
  imports: [
    OnyxTabsComponent,
    OrderModalOutsourcingFormComponent,
    TranslatePipe,
    OnyxEmptyStateComponent,
    OnyxDropdownComponent,
    OnyxButtonComponent,
    OnyxMessageComponent,
    OnyxLogoComponent,
    OnyxIconComponent,
    OnyxDividerComponent,
    OnyxInformationHeadingComponent,
    OrderModalAssignmentComponent,
    OnyxIconButtonComponent,
    OnyxDatePipe,
    OrderModalDeadlineMessageComponent,
    OnyxTooltipDirective,
  ],
  templateUrl: './order-modal-summary-main-section.component.html',
  styleUrl: './order-modal-summary-main-section.component.scss',
})
export class OrderModalSummaryMainSectionComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly ordersService = inject(OrdersService);
  private readonly toastService = inject(OnyxToastService);
  private readonly fleetService = inject(FleetService);
  private readonly fleetValidationHelper = inject(FleetValidationHelper);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly router = inject(Router);
  private readonly orderHelper = inject(OrderHelper);
  private readonly orderModalHelper = inject(OrderModalHelper);
  private readonly authStore = inject(AuthStore);

  public readonly order = input.required<Order>();
  public readonly mode = input.required<OnyxOptional<OrderPlanningMode>>();
  public readonly close$ = input.required<Subject<void>>();

  protected readonly I18N = 'orders.orderModal';

  protected readonly OrderPlanningMode = OrderPlanningMode;
  protected readonly OrderStatus = OrderStatus;
  protected readonly OnyxFormMode = OnyxFormMode;

  protected tab = linkedSignal(() =>
    this.missingAssignment()
      ? OrderPlanningMode.MANUAL
      : (this.mode() ?? OrderPlanningMode.AUTO),
  );
  protected vehicle = signal<SimplifiedFleet | null>(null);
  protected trailer = signal<SimplifiedFleet | null>(null);
  protected loading = signal(false);
  protected forceLoading = signal(false);

  protected tabs = computed<OnyxTab<OrderPlanningMode>[]>(() => {
    const buildTab = (
      tab: OnyxTab<OrderPlanningMode>,
    ): OnyxTab<OrderPlanningMode> => ({
      ...tab,
      disabled: tab.disabled || !!tab.tooltip,
    });

    const hasEngineProposal = this.hasEngineProposal();
    const hasManualProposal = !!this.manualProposal();
    const isExclusive = this.order().parameters.isExclusive;

    return [
      buildTab({
        name: `${this.I18N}.automaticVehicleChoice`,
        value: OrderPlanningMode.AUTO,
        icon: { name: 'signet-light', size: 20 },
        iconColor: 'black',
        iconPulse:
          hasEngineProposal && this.tab() !== OrderPlanningMode.AUTO
            ? 'blue'
            : undefined,
        tooltip: hasManualProposal
          ? `${this.I18N}.disabledButManualProposal`
          : this.isPast()
            ? `${this.I18N}.timeToStartHasPassed`
            : this.missingAssignment()
              ? `${this.I18N}.temporaryDisabled`
              : undefined,
        disabled: this.isPast() || !!this.missingAssignment(),
      }),
      buildTab({
        name: `${this.I18N}.manualVehicleChoice`,
        value: OrderPlanningMode.MANUAL,
        icon: { name: 'manual-filled', size: 20 },
        iconColor: 'violet',
        tooltip:
          hasEngineProposal && !hasManualProposal
            ? `${this.I18N}.disabledButEngineProposal`
            : undefined,
      }),
      buildTab({
        name: 'labels.outsourcing',
        value: OrderPlanningMode.OUTSOURCING,
        icon: { name: 'cursor-filled', size: 20 },
        iconColor: 'sea-green',
        tooltip: isExclusive
          ? `${this.I18N}.disabledIsExclusive`
          : hasEngineProposal
            ? `${this.I18N}.disabledButEngineProposal`
            : hasManualProposal
              ? `${this.I18N}.disabledButManualProposal`
              : undefined,
      }),
    ];
  });

  protected status = computed(() => this.order().status.value);
  protected draftState = computed(() => {
    const order = this.order();
    if (!OrderHelper.isDraft(order)) return null;

    const isPausedDraft = OrderHelper.isPausedDraft(order);
    const senderDescription = isPausedDraft
      ? 'pausedDraft'
      : 'notSentToCarriers';

    return {
      heading: `${DictionaryCode.ORDER_STATUS}.${OrderStatus.DRAFT}`,
      description: `orders.draft.${this.authStore.isSender() ? senderDescription : 'notPlanned'}`,
      button: isPausedDraft
        ? {
            text: 'orders.draft.resumeSearch',
            action: () => this.resumeSearch(),
          }
        : { text: 'labels.planRoute', action: () => this.planRoute() },
    };
  });
  protected hasApprovedVehicle = computed(() =>
    OrderHelper.hasApprovedVehicle(this.order().status.value),
  );
  protected hasEngineProposal = computed(
    () => !!OrderHelper.getEngineProposal(this.order()),
  );

  protected manualProposal = linkedSignal(() =>
    OrderHelper.getManualProposal(this.order()),
  );
  protected isManualProposalLoading = computed(
    () => this.manualProposal()?.result === null,
  );
  protected selectedManualVehicleUuid = linkedSignal(
    () => this.manualProposal()?.vehicle.uuid ?? null,
  );
  protected selectedManualVehicle = signal<Fleet | null>(null);
  protected hasTrailerConflict = computed(
    () =>
      this.hasLoadedSemiTrailer() &&
      this.selectedManualVehicle()?.trailer != null &&
      this.order().basicInformation.loadedSemiTrailer?.uuid !==
        this.trailer()?.uuid,
  );

  protected startPlanningData = computed(() =>
    OrderHelper.getStartPlanningData(this.order()),
  );
  protected currentPointServiceTime = computed(
    () => OrderHelper.getCurrentPoint(this.order().points)?.serviceTime,
  );
  protected isPast = computed(() => {
    if (this.vehicle() || this.trailer()) return false;

    const points = this.order().points;
    const currentPoint = OrderHelper.getCurrentPoint(points);
    const deadline = RouteHelper.getPointDeadlineTime(currentPoint);

    return !!deadline && CommonHelper.isPast(deadline);
  });
  protected missingAssignment = computed(() =>
    OrderHelper.getMissingAssignment(this.order()),
  );

  protected vehiclesSource = computed<OnyxDropdownOptionsSource<Fleet>>(() => {
    const { categories, isLowDeck } = this.searchingVehicleData();
    return {
      list: (query, pagination) => {
        const showSet = !!isLowDeck && !this.hasLoadedSemiTrailer();

        return this.fleetService.reload$.pipe(startWith(undefined)).pipe(
          switchMap(() =>
            this.fleetService.searchFleet(
              query,
              {
                ...pagination,
                filters: [
                  FleetHelper.createStateFilter(FleetState.ACTIVE),
                  FleetHelper.createCategoryFilter(categories),
                  ...(showSet
                    ? [
                        {
                          key: 'isSet',
                          operator: OnyxFilterOperator.EQ as const,
                          value: true,
                        },
                      ]
                    : []),
                ],
              },
              { showSet, showAssignedTrailer: true },
            ),
          ),
          map((result) => {
            return {
              ...result,
              options: chain(DropdownHelper.mapToOptions(result.options)!)
                .map((option) => {
                  const optionTrailer = option.value.trailer;
                  const order = this.order();

                  return this.fleetValidationHelper.validateOrderManualSelect({
                    context: ValidationContext.ORDER,
                    order,
                    vehicle: option.value,
                    trailer:
                      this.trailer() ??
                      (!this.hasLoadedSemiTrailer() && !!optionTrailer
                        ? optionTrailer
                        : null),
                    hasTrailerConflict:
                      this.hasLoadedSemiTrailer() &&
                      !!optionTrailer &&
                      optionTrailer.uuid !==
                        order.basicInformation.loadedSemiTrailer?.uuid,
                    option,
                  }).option!;
                })
                .thru((options) =>
                  ValidationHelper.groupOptionsByIssue(options, {
                    error: 'labels.incompatible',
                    valid: 'labels.meetRequirements',
                  }),
                )
                .value(),
            };
          }),
        );
      },
      get: (uuid) =>
        this.fleetService.getFleet(uuid).pipe(
          map((vehicle) => ({
            name: `${this.fleetIdentifierPipe.transform(vehicle)}${vehicle.trailer ? ` / ${this.fleetIdentifierPipe.transform(vehicle.trailer)}` : ''}`,
            value: vehicle,
          })),
        ),
      idKey: 'uuid',
    };
  });

  private searchingVehicleData = computed(() =>
    OrderModalHelper.getSearchingVehicleData(
      this.order(),
      this.translateService,
    ),
  );
  private hasLoadedSemiTrailer = computed(() =>
    OrderHelper.hasLoadedSemiTrailer(this.order()),
  );

  public ngOnInit(): void {
    this.tab.update((tab) => {
      if (this.hasEngineProposal() && !this.manualProposal()) {
        return OrderPlanningMode.AUTO;
      }
      if (this.manualProposal() || this.isPast()) {
        return OrderPlanningMode.MANUAL;
      }
      return tab;
    });

    effect(
      () => {
        const order = this.order();
        const selectedManualVehicle = this.selectedManualVehicle();

        let data: Pick<AssignationData, 'vehicle' | 'trailer'> | null = null;
        if (order.assignedData) {
          data = order.assignedData;
        } else if (
          selectedManualVehicle &&
          this.tab() === OrderPlanningMode.MANUAL
        ) {
          data = {
            vehicle: selectedManualVehicle,
            trailer: selectedManualVehicle.trailer ?? null,
          };
        } else if (this.hasEngineProposal()) {
          data = order.proposalData;
        }

        this.vehicle.set(data?.vehicle ?? null);
        this.trailer.set(data?.trailer ?? null);
      },
      { injector: this.injector },
    );
  }

  protected addManualProposal(): void {
    const selectedManualVehicle = this.selectedManualVehicle();
    if (!selectedManualVehicle) return;

    this.loading.set(true);
    this.fleetService
      .getAssignedRoute(selectedManualVehicle.uuid)
      .pipe(
        switchMap((assignedRoute) => {
          if (assignedRoute.length) {
            this.close$().next();
            this.router.navigateByUrl(
              OrdersRoute.ASSIGN_MANUAL_ROUTE.replace(
                ':uuid',
                this.order().uuid,
              ),
              {
                state: {
                  [OrdersRouterStateKey.ORDER]: this.order(),
                  [OrdersRouterStateKey.FLEET]: selectedManualVehicle,
                  [OrdersRouterStateKey.FLEET_ROUTE]: assignedRoute,
                } satisfies Partial<OrderManualRouteResolverData>,
              },
            );
            return EMPTY;
          }

          const route: SimplifiedRoutePoint[] = this.order()
            .points.filter((point) => !RouteHelper.isFinished(point))
            .map((point) => ({
              orderUuid: this.order().uuid,
              orderPointUuid: point.uuid,
              type: point.category,
            }));

          return this.ordersService.addManualProposal(
            this.order().uuid,
            selectedManualVehicle.uuid,
            route,
          );
        }),
      )
      .subscribe((proposal) => this.manualProposal.set(proposal))
      .add(() => this.loading.set(false));
  }

  protected forceManualProposal(): void {
    const selectedManualVehicleUuid = this.selectedManualVehicleUuid();
    if (!selectedManualVehicleUuid || !this.isManualProposalLoading()) return;

    this.forceLoading.set(true);
    this.ordersService
      .forceManualProposal(this.order().uuid, selectedManualVehicleUuid)
      .subscribe(() =>
        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.vehicleAccepted`,
          buttons: [
            {
              text: 'labels.planRoute',
              callback: () => this.planRoute(),
              color: 'black',
              variant: 'ghost',
              size: 24,
            },
          ],
        }),
      )
      .add(() => this.forceLoading.set(false));
  }

  protected deleteManualProposal(): void {
    this.loading.set(true);
    this.ordersService
      .deleteManualProposal(this.order().uuid)
      .subscribe(() => this.manualProposal.set(null))
      .add(() => this.loading.set(false));
  }

  protected confirmManualProposal(): void {
    const selectedManualVehicle = this.selectedManualVehicle();
    if (!selectedManualVehicle) return;

    this.loading.set(true);
    this.ordersService
      .confirmManualProposal(this.order().uuid, selectedManualVehicle.uuid)
      .subscribe(() =>
        this.toastService.showSuccess({
          message: 'drivers.assignVehicleModal.success',
        }),
      )
      .add(() => this.loading.set(false));
  }

  protected planRoute(): void {
    this.orderHelper.edit(this.order().uuid, {
      options: { close$: this.close$() },
      params: { [OrdersQueryParamKey.PLAN_ROUTE]: true },
    });
  }

  protected resumeSearch(): void {
    this.loading.set(true);
    this.orderHelper
      .resumeSearch(this.order(), {})
      .subscribe()
      .add(() => this.loading.set(false));
  }

  protected unassignTrailer(): void {
    const selectedManualVehicle = this.selectedManualVehicle();
    if (!selectedManualVehicle) return;

    this.loading.set(true);
    this.fleetService
      .assignTrailer(selectedManualVehicle.uuid, null)
      .subscribe(() => {
        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.semiTrailerUnpinned`,
        });
        this.selectedManualVehicleUuid.set(null);

        setTimeout(() =>
          this.selectedManualVehicleUuid.set(selectedManualVehicle.uuid),
        );
      })
      .add(() => this.loading.set(false));
  }

  protected openActivityModal(): void {
    this.orderModalHelper.openActivityModal({ order: this.order() });
  }
}
